import { getApiGen } from "$root/api/factory";
import { repositoryHelperFactory } from "$root/api/repository";
import { axiosExtract } from "$root/third-party-integrations/axios";
import {
	CustomAttributesControllerV1ApiFactory,
	type CustomAttributeTagRenameRequest,
	type CustomAttributeTagUsageRequest,
} from "@eu.mdotm.api/custom-attributes";
import type { CustomAttributeDto } from "./type";

const workspaceRepositoryBaseKeys = {
	list: "list",
};

const { newQuery, newAction, invalidateEntity } = repositoryHelperFactory("customAttributes", {
	listKeys: Object.values(workspaceRepositoryBaseKeys),
});

const customAttributesApi = () => getApiGen(CustomAttributesControllerV1ApiFactory);

export const investmentCustomAttributesRepository = {
	checkNameAvailability: newQuery("checkNameAvailability", (name: string) =>
		axiosExtract(customAttributesApi().isAttributeNameAvailable(name)),
	),

	customAttributes: newQuery(
		workspaceRepositoryBaseKeys.list,
		(): Promise<CustomAttributeDto[]> => axiosExtract(customAttributesApi().retrieveAllCustomAttributes()),
	),

	getCustomAttribute: newQuery("getCustomAttribute", async (attributeUuid: string) => {
		const customAttributes: CustomAttributeDto[] = await axiosExtract(
			customAttributesApi().retrieveAllCustomAttributes(),
		);
		return customAttributes.find((attribute) => attribute.attributeUuid === attributeUuid);
	}),

	createCustomAttribute: newAction((customAttributeDto: CustomAttributeDto) =>
		axiosExtract(customAttributesApi().createCustomAttribute(customAttributeDto)),
	),

	updateCustomAttribute: newAction(async (uuid: string, customAttributeDto: CustomAttributeDto) => {
		await axiosExtract(customAttributesApi().updateCustomAttribute({ ...customAttributeDto, attributeUuid: uuid }));
		invalidateEntity(uuid);
	}),

	verifyTagOptionUsage: newQuery("verifyTagOptionUsage", async (payload: CustomAttributeTagUsageRequest) =>
		axiosExtract(customAttributesApi().checkTagValueUsage(payload)),
	),

	/**
	 * Rename one tag option, as an operation of its own.
	 *
	 * It cannot go through {@link updateCustomAttribute}: that endpoint compares options by name, so a
	 * renamed option reads as "the old one disappeared, a new one arrived" and it strips the tag from every
	 * portfolio carrying it and drops the criterion from the filters of every workspace of every user of the
	 * client. This endpoint replaces the value in one transaction instead.
	 */
	renameTagOption: newAction(async (payload: CustomAttributeTagRenameRequest) => {
		const result = await axiosExtract(customAttributesApi().renameCustomAttributeTagValue(payload));
		invalidateEntity(payload.attributeUuid);
		return result;
	}),

	deleteCustomAttribute: newAction(async (uuid: string) => {
		await axiosExtract(customAttributesApi().deleteCustomAttribute(uuid));
		invalidateEntity(uuid);
	}),
};
